import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '@shared/hooks/useStore';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, PasswordInput } from '@shared/components/common/Input';
import { Button } from '@shared/components/common/Button';
import { Label } from '@shared/components/common/Label';
import { ADMIN_ROUTES, SCREEN_TYPE } from '@shared/configs/constants';
import { MailIcon } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

const loginSchema = yup.object({
    email: yup.string().email('メールアドレスの形式が正しくありません').required('メールアドレスは必須です'),
    password: yup.string().required('パスワードは必須です')
});

type LoginFormData = yup.InferType<typeof loginSchema>;

const LoginPage = observer(() => {
    const { authStore } = useStore();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (authStore.isLoggedIn && authStore.isGroupAdmin) {
            navigate(ADMIN_ROUTES.userManagement.href, { replace: true });
        }
    }, [authStore.isLoggedIn, authStore.isGroupAdmin, navigate]);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<LoginFormData>({
        resolver: yupResolver(loginSchema)
    });

    const onSubmit = async (data: LoginFormData) => {
        setIsLoading(true);
        try {
            const success = await authStore.login({
                email: data.email,
                password: data.password,
                screenType: SCREEN_TYPE.GA
            });
            if (success) {
                navigate(ADMIN_ROUTES.userManagement.href, { replace: true });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='min-h-screen flex items-center justify-center bg-[#F1F1F1]'>
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] md:p-10 p-4 w-[calc(100%-2rem)] sm:max-w-[640px]'>
                <h1 className='md:text-4xl text-3xl text-primary font-bold text-center mb-8'>ログイン</h1>

                <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
                    <div className='space-y-2'>
                        <Label htmlFor='email' required>メールアドレス</Label>

                        <div className='relative'>
                            <Input
                                id='email'
                                type='email'
                                {...register('email')}
                                className={cn('h-14 pr-10', errors.email && 'border-red-500 focus-visible:ring-red-500')}
                                placeholder='admin@example.com'
                            />
                            <MailIcon className='absolute w-6 h-6 right-3 top-4' />
                        </div>
                        {errors.email && (
                            <p className='text-red-500 text-sm'>{errors.email.message}</p>
                        )}
                    </div>

                    <div className='space-y-2'>
                        <Label htmlFor='password' required>パスワード</Label>
                        <PasswordInput
                            id='password'
                            type={showPassword ? 'text' : 'password'}
                            {...register('password')}
                            className={cn('h-14 pr-10', errors.password && 'border-red-500 focus-visible:ring-red-500')}
                            placeholder='••••••••'
                            onToggleIcon={() => setShowPassword(!showPassword)}
                        />
                        {errors.password && (
                            <p className='text-red-500 text-sm'>{errors.password.message}</p>
                        )}
                    </div>

                    <div className='text-center flex justify-end'>
                        <Link to={ADMIN_ROUTES.forgotPassword.href}>
                            <Button variant='link' type='button'>
                                パスワードを忘れた方はこちら
                            </Button>
                        </Link>
                    </div>
                    <Button
                        type='submit'
                        disabled={isLoading}
                        className='w-full h-[56px] text-base mt-6'
                    >
                        {isLoading ? 'ログイン中...' : 'ログイン'}
                    </Button>
                </form>
            </div>
        </div>
    );
});

export default LoginPage;
